import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import calendar from '../../assets/img/svg/calendar-icon.svg'
import clock from '../../assets/img/svg/clock-icon.svg'
import { getAllRegions, getAllSchedules, getSchedulesPropertyBydate } from '../../redux/slices/scheduleSlice'
import { useDispatch, useSelector } from 'react-redux'
import { SpinnerContext } from '../../context/spinnerContext'
import {
  appendImageURL,
  capitalizeAndRemoveUnderscore,
  convertTimestampToCalenderDate,
  convertTimestampToDate,
  convertTimestampToTime,
  getCurrentDayTimestamps,
  getDayStartAndEndTimestamps,
  getTimeStamp,
  OVERDUE
} from '../../helper/function'
import { getAllPropertiesName } from '../../redux/slices/propertySlice'
import { PropertyCardSliderDefaultImg } from '../../helper/imagepath'
import Carousel from 'react-bootstrap/Carousel'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'
import CleaningScheduleDetail from '../../component/Property/Schedule/cleaningScheduleModel'
import { useNavigate } from 'react-router-dom'
import Pagination from '../../component/common/pagination'
import FullCalendarView from '../../component/common/fullCalendarView'
import dayjs from 'dayjs'

const Schedules = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const scrollRef = useRef()
  const { changeLoader } = useContext(SpinnerContext)

  const { allSchedules, allRegions, scheduleProperty } = useSelector(({ schedule }) => schedule)
  const { allPropertiesName } = useSelector(({ property }) => property)

  // State Management
  const [selectedRegion, setSelectedRegion] = useState(null)
  const [selectedProperty, setSelectedProperty] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedDate, setSelectedDate] = useState('')
  const [dateRange, setDateRange] = useState(null)
  const [scheduleID, setScheduleID] = useState(null)
  const [scheduleData, setScheduleData] = useState(null)
  const [showMoreDetailModal, setShowMoreDetailModal] = useState(false)
  const [isCanceledBookingStatus, setIsCanceledBookingStatus] = useState(false)
  const [isRefresh, setIsRefresh] = useState(true)
  const [isScroll, setIsScroll] = useState(false)

  const itemsPerPage = useMemo(() => 3, [])
  const { startDayTimestamp, endDayTimestamp } = useMemo(() => getCurrentDayTimestamps(), [])
  const [startTimestamp, setStartTimestamp] = useState(startDayTimestamp)
  const [endTimestamp, setEndTimestamp] = useState(endDayTimestamp)

  // Toggle More Detail Modal
  const toggleMoreDetailModal = () => setShowMoreDetailModal(!showMoreDetailModal)

  // Handle Scroll to Ref
  const handleScrollRef = useCallback(() => {
    const divElement = document.getElementById('scheduleScrollId')
    divElement?.scrollIntoView({ behavior: 'smooth' })
    setIsScroll(false)
  }, [document])

  useEffect(() => {
    if (isScroll && scrollRef.current) {
      handleScrollRef()
    }
  }, [isScroll, scheduleProperty])

  // Initial Data Fetch
  useEffect(() => {
    const fetchData = async () => {
      changeLoader(true)
      try {
        await Promise.all([dispatch(getAllRegions()), dispatch(getAllPropertiesName())])
      } catch (err) {
        console.error('Error fetching data:', err)
      } finally {
        changeLoader(false)
      }
    }
    fetchData()
  }, [])

  // Fetch Schedules Function
  const fetchSchedules = async (params) => {
    changeLoader(true)
    try {
      dispatch(getAllSchedules(params))
      setIsRefresh(false)
    } catch (err) {
      console.error('Error fetching schedules:', err)
    } finally {
      changeLoader(false)
      setIsRefresh(false)
    }
  }

  // Fetch Schedules by Date
  const fetchSchedulesbyDate = async (params) => {
    changeLoader(true)
    try {
      dispatch(getSchedulesPropertyBydate(params))
      setIsRefresh(false)
    } catch (err) {
      console.error('Error fetching data:', err)
    } finally {
      changeLoader(false)
      setSelectedDate('')
    }
  }

  // Handle Date Click
  const handleDateClick = async (arg) => {
    let result = CALENDAR_EVENT?.find((val) => val.start === (arg?.dateStr || arg?.event?.startStr))
    if (!result) {
      return
    }
    setIsScroll(true)
    setSelectedDate(arg)
    const formattedDate = getTimeStamp(dayjs(arg?.date || arg?.event?.start).format('YYYY-MM-DDTHH:mm'))
    const { startOfDayTimestamp, endOfDayTimestamp } = getDayStartAndEndTimestamps(formattedDate)

    setStartTimestamp(startOfDayTimestamp)
    setEndTimestamp(endOfDayTimestamp)
    setCurrentPage(1)

    const params = {
      page: 1,
      limit: itemsPerPage,
      sort_column: 'created_at',
      sort_order: 'ASC',
      startTimestamps: startOfDayTimestamp,
      endTimestamps: endOfDayTimestamp,
      ...(selectedRegion && { region_id: selectedRegion }),
      ...(selectedProperty && { properties_id: selectedProperty })
    }

    await fetchSchedulesbyDate(params)
  }

  // Fetch Schedules on Date Range Change
  useEffect(() => {
    if (!isRefresh) return

    const { startOfDayTimestamp } = getDayStartAndEndTimestamps(getTimeStamp(dateRange?.start))
    const { endOfDayTimestamp } = getDayStartAndEndTimestamps(getTimeStamp(dateRange?.end))

    const params = {
      startTimestamps: startOfDayTimestamp,
      endTimestamps: endOfDayTimestamp,
      ...(selectedRegion && { region_id: selectedRegion }),
      ...(selectedProperty && { properties_id: selectedProperty })
    }

    if (dateRange) fetchSchedules(params)
  }, [selectedRegion, selectedProperty, dateRange, isRefresh])

  // Fetch Schedules on Initial Load and Date Range Change
  useEffect(() => {
    if (!dateRange?.start || !dateRange?.end || selectedDate || isScroll || isRefresh) return

    setSelectedDate('')

    const params = {
      page: currentPage,
      limit: itemsPerPage,
      sort_column: 'created_at',
      sort_order: 'ASC',
      startTimestamps: startTimestamp,
      endTimestamps: endTimestamp,
      ...(selectedRegion && { region_id: selectedRegion }),
      ...(selectedProperty && { properties_id: selectedProperty })
    }

    fetchSchedulesbyDate(params)
  }, [selectedProperty, selectedRegion, dateRange, currentPage, startTimestamp, endTimestamp, isRefresh])

  // Trigger Fetch when Selected Date Changes
  // useEffect(() => {
  //   console.log('selectedDate :>> ', selectedDate)
  //   console.log('currentPage :>> ', currentPage)
  //   if (selectedDate) {
  //     setCurrentPage(1)
  //     handleDateClick(selectedDate)
  //   }
  // }, [selectedRegion, selectedProperty, currentPage, itemsPerPage, isRefresh])

  // Handlers to Set Region, Property, and Date Range, Triggering Refresh as Needed
  const handleSelectRegion = (region_id) => {
    setSelectedRegion(region_id)
    setIsRefresh(true)
  }

  const handleSelectProperty = (propertyId) => {
    setSelectedProperty(propertyId)
    setIsRefresh(true)
  }

  const onDatasetChange = useCallback((arg) => {
    setDateRange({ start: arg?.start, end: arg?.end })
    setIsRefresh(true)
    setSelectedDate('')
    setCurrentPage(1)
    setStartTimestamp(startDayTimestamp)
    setEndTimestamp(endDayTimestamp)
  }, [])

  const CALENDAR_EVENT = useMemo(
    () =>
      allSchedules?.scheduledProperties
        ?.map((booking) => {
          // Convert booking_date_time and next_checkin_time from seconds to milliseconds
          const startTimestamp = booking.booking_date_time ? booking.booking_date_time : null
          const startDate = startTimestamp ? startTimestamp : null
          const classNameMap = {
            scheduled: 'event-scheduled',
            completed: 'event-completed',
            canceled: 'event-canceled',
            'canceled cleaner': 'event-cleaner-canceled'
          }

          // Return only if start date is valid
          if (startDate) {
            return {
              id: booking.property_schedule_id,
              title: capitalizeAndRemoveUnderscore(booking?.booking_status),
              start: startDate ? convertTimestampToCalenderDate(startDate) : null,
              className: classNameMap[booking?.booking_status] || 'event-other'
            }
          }

          return null
        })
        .filter(Boolean), // Filter out any null values
    [allSchedules]
  )

  const handlePageChange = (selectedItem) => setCurrentPage(selectedItem.selected + 1)

  return (
    <div className="page-wrapper">
      <div className="schedule_topbar">
        <div className="schedule_sec">
          <div className="regions_sec">
            <Dropdown>
              <Dropdown.Toggle className="dropdown_opction_one" variant="success" id="dropdown-basic">
                {selectedRegion ? allRegions?.find((region) => region.region_id === selectedRegion)?.region : 'Regions'}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item key={'all'} onClick={() => handleSelectRegion(null)}>
                  All Regions
                </Dropdown.Item>
                {allRegions?.map((region) => (
                  <Dropdown.Item key={region.region_id} onClick={() => handleSelectRegion(region.region_id)}>
                    {region.region}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <div className="regions_sec">
            <Dropdown>
              <Dropdown.Toggle className="dropdown_opction_one" variant="success" id="dropdown-basic">
                {selectedProperty ? allPropertiesName?.find((prop) => prop.properties_id === selectedProperty).property_name : 'Properties'}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleSelectProperty(null)}>All Properties</Dropdown.Item>
                {allPropertiesName?.map((property) => (
                  <Dropdown.Item
                    key={property.properties_id}
                    className="dropdown_opction"
                    onClick={() => handleSelectProperty(property.properties_id)}
                  >
                    <div className="dropdown_btn_menu_item_div">{property.property_name}</div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="property_sec_bgbox">
        <FullCalendarView events={CALENDAR_EVENT} handleDateClick={handleDateClick} onDatasetChange={onDatasetChange} />
      </div>
      {scheduleProperty?.scheduledProperties?.length > 0 && (
        <>
          <div className="Schedule_slider_sec" id="scheduleScrollId" ref={scrollRef}>
            <div className="sliding_box row">
              {scheduleProperty?.scheduledProperties?.map((schedule, index) => {
                let CheckTimeOver = schedule?.booking_status !== 'completed' ? getTimeStamp(new Date()) >= schedule?.booking_date_time : false
                return (
                  <div className="col-md-6 col-lg-6 col-xl-4" key={index}>
                    <div className="Schedule_slider_sec_box">
                      <div className="slider_main_div">
                        <Carousel data-bs-theme="dark">
                          {schedule?.properties?.property_image?.length > 0 ? (
                            schedule?.properties?.property_image.map((image, index) => (
                              <Carousel.Item key={index}>
                                <img
                                  className={`property_slider_img`}
                                  src={image ? appendImageURL(image) : PropertyCardSliderDefaultImg}
                                  alt="not found"
                                />
                              </Carousel.Item>
                            ))
                          ) : (
                            <Carousel.Item key={index}>
                              <img className={`property_slider_img`} src={PropertyCardSliderDefaultImg} alt="not found" />
                            </Carousel.Item>
                          )}
                        </Carousel>
                      </div>

                      <div>
                        <h4 className="slider_data_text">{schedule?.properties?.property_name}</h4>
                        <div className="Schedule_slider_dataset">
                          <div className="Schedule_slider_data_calendar">
                            <img className="calendar_img" src={calendar} alt="calendar" />{' '}
                            <h5 className="date_text">{convertTimestampToDate(schedule?.booking_date_time)}</h5>
                          </div>
                          <div className="Schedule_slider_data_clock">
                            <img className="clock_img" src={clock} alt="clock" />
                            <h5 className="date_text"> {convertTimestampToTime(schedule?.booking_date_time)}</h5>
                          </div>
                        </div>
                        <p className="Status_text">
                          Status: <span className="Scheduled_text"> {capitalizeAndRemoveUnderscore(schedule?.booking_status)}</span>{' '}
                          {!['completed', 'canceled', 'canceled cleaner']?.includes(schedule?.booking_status) && (
                            <span className="red_color"> {CheckTimeOver ? OVERDUE : ''}</span>
                          )}
                        </p>
                      </div>

                      <button
                        className="btn btn-primary w-100 mt-3"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          setScheduleData(schedule)
                          setScheduleID(schedule?.property_schedule_id)
                          setIsCanceledBookingStatus(false)
                          if (schedule?.booking_status === 'scheduled') {
                            toggleMoreDetailModal()
                          } else if (['canceled cleaner', 'completed']?.includes(schedule?.booking_status)) {
                            navigate(`/cleaning-details/${schedule?.property_schedule_id}`)
                          } else if (['canceled', 'in progress']?.includes(schedule?.booking_status)) {
                            setIsCanceledBookingStatus(true)
                            toggleMoreDetailModal()
                          } else {
                            return
                          }
                        }}
                      >
                        More Details
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            totalPages={scheduleProperty?.totalPage || 0}
            itemsPerPage={itemsPerPage}
          />
        </>
      )}
      {showMoreDetailModal && (
        <Modal className="scheduledetail_modal_main_div" size="md" centered isOpen={showMoreDetailModal} toggle={toggleMoreDetailModal}>
          <ModalHeader toggle={toggleMoreDetailModal}>{scheduleData?.properties?.property_name}</ModalHeader>
          <ModalBody>
            <CleaningScheduleDetail
              scheduleID={scheduleID}
              setIsRefresh={setIsRefresh}
              toggleMoreDetailModal={toggleMoreDetailModal}
              isCanceledBookingStatus={isCanceledBookingStatus}
            />
          </ModalBody>
        </Modal>
      )}
    </div>
  )
}

export default Schedules
